import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	create_agent,
	get_agents,
	upload_agent_calibration,
} from "../../apis/helper";
import { BlockButton as UntypedBlockButton } from "../../inJoy-components/Button/BlockButton";
import { IconButton } from "../../inJoy-components/Button/IconButton";
import { Icon } from "../../inJoy-components/Icon/Icon";
import { Input as UntypedInput } from "../../inJoy-components/Input/Input";
import { CourtLinesUpload } from "./CourtLinesUpload";

const Input = UntypedInput as any;
const BlockButton = UntypedBlockButton as any;

type Step =
	| "naming"
	| "generating"
	| "api_key"
	| "connecting"
	| "connected"
	| "calibration"
	| "timeout"
	| "leave_confirm";

interface AddCameraModalProps {
	isOpen: boolean;
	onClose: () => void;
	onCreated: (agentId: string) => void;
	/** For Storybook only — sets the initial step */
	_initialStep?: Step;
	/** For Storybook only — pre-fills form data */
	_initialData?: {
		name?: string;
		desc?: string;
		agentId?: string;
		apiKey?: string;
	};
}

const POLL_INTERVAL = 5000;
const POLL_TIMEOUT = 10 * 60 * 1000;

const ProgressIndicator = ({
	current,
	total,
	t,
}: {
	current: number;
	total: number;
	t: (key: string, opts?: any) => string;
}) => (
	<div className="flex flex-col gap-[8px] items-start w-full">
		<p className="text-[14px] font-normal leading-[1.4] tracking-[0.25px] text-[#6d6d6d] text-center">
			{t("events.add_camera.step_of", { current, total })}
		</p>
		<div className="flex gap-[8px] items-center w-full">
			{Array.from({ length: total }, (_, i) => (
				<div
					key={i}
					className={`flex-1 h-[4px] rounded-full ${i < current ? "bg-black" : "bg-[#dfdfdf]"}`}
				/>
			))}
		</div>
	</div>
);

export const AddCameraModal = ({
	isOpen,
	onClose,
	onCreated,
	_initialStep,
	_initialData,
}: AddCameraModalProps) => {
	const { t } = useTranslation();
	const [step, setStep] = useState<Step>(_initialStep || "naming");
	const [name, setName] = useState(_initialData?.name || "");
	const [desc, setDesc] = useState(_initialData?.desc || "");
	const [agentId, setAgentId] = useState(_initialData?.agentId || "");
	const [apiKey, setApiKey] = useState(_initialData?.apiKey || "");
	const [calibrationFile, setCalibrationFile] = useState<File | null>(null);
	const [error, setError] = useState<string | null>(null);
	const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const prevStepRef = useRef<Step>("naming");

	// Reset on open + lock body scroll
	useEffect(() => {
		if (isOpen) {
			setStep(_initialStep || "naming");
			setName(_initialData?.name || "");
			setDesc(_initialData?.desc || "");
			setAgentId(_initialData?.agentId || "");
			setApiKey(_initialData?.apiKey || "");
			setCalibrationFile(null);
			setError(null);
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}
		return () => {
			document.body.style.overflow = "unset";
		};
	}, [isOpen, _initialStep, _initialData]);

	const stopPolling = useCallback(() => {
		if (pollingRef.current) {
			clearInterval(pollingRef.current);
			pollingRef.current = null;
		}
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	// Cleanup on unmount
	useEffect(() => stopPolling, [stopPolling]);

	const handleClose = () => {
		if (step === "api_key") {
			prevStepRef.current = step;
			setStep("leave_confirm");
		} else {
			stopPolling();
			onClose();
		}
	};

	const handleGenerate = async () => {
		setStep("generating");
		setError(null);
		try {
			const result = await create_agent({ name, desc });
			setAgentId(result.agent_profile_id);
			setApiKey(result.key);
			setStep("api_key");
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to create agent");
			setStep("naming");
		}
	};

	const startPolling = useCallback(
		(targetAgentId: string) => {
			stopPolling();

			pollingRef.current = setInterval(async () => {
				try {
					const agents = await get_agents();
					const agent = agents.find((a) => a.agent_id === targetAgentId);
					if (agent?.is_healthy) {
						stopPolling();
						setStep("connected");
					}
				} catch {
					// Silently retry on network errors
				}
			}, POLL_INTERVAL);

			timeoutRef.current = setTimeout(() => {
				stopPolling();
				setStep("timeout");
			}, POLL_TIMEOUT);
		},
		[stopPolling],
	);

	const handleConnect = () => {
		setStep("connecting");
		startPolling(agentId);
	};

	// Auto-advance from connected to calibration after 1s
	useEffect(() => {
		if (step === "connected") {
			const timer = setTimeout(() => setStep("calibration"), 1000);
			return () => clearTimeout(timer);
		}
	}, [step]);

	const handleFinish = async () => {
		stopPolling();
		if (calibrationFile && agentId) {
			try {
				await upload_agent_calibration(agentId, calibrationFile);
			} catch (err) {
				console.warn("⚠️ Failed to upload calibration to agent:", err);
			}
		}
		onCreated(agentId);
		onClose();
	};

	const [copiedField, setCopiedField] = useState<string | null>(null);

	const copyToClipboard = async (text: string, field: string) => {
		try {
			await navigator.clipboard.writeText(text);
			setCopiedField(field);
			setTimeout(() => setCopiedField(null), 2000);
		} catch {
			// Fallback silently
		}
	};

	if (!isOpen) return null;

	const renderStep = (targetStep: Step) => {
		switch (targetStep) {
			case "naming":
				return (
					<>
						<Header
							title={t("events.add_camera.title")}
							onClose={handleClose}
						/>
						<div className="flex flex-col gap-[24px] items-start w-full">
							<ProgressIndicator current={1} total={3} t={t} />
							<div className="flex flex-col gap-[12px] items-start w-full">
								<Input
									label={
										<div className="flex items-center gap-[2px]">
											<span>{t("events.add_camera.name_label")}</span>
											<span className="text-[#e90035]">*</span>
										</div>
									}
									placeholder={t("events.add_camera.name_placeholder")}
									value={name}
									onChange={(e: any) => setName(e.target.value)}
									hint={t("events.add_camera.name_hint")}
									error={error}
								/>
								<Input
									label={t("events.add_camera.desc_label")}
									placeholder={t("events.add_camera.desc_placeholder")}
									value={desc}
									onChange={(e: any) => setDesc(e.target.value)}
								/>
							</div>
						</div>
						<div className="flex items-center justify-end w-full">
							<BlockButton
								type="Primary"
								width="150px"
								size="Large"
								onClick={handleGenerate}
								disabled={!name.trim()}
							>
								{t("events.add_camera.generate_key")}
							</BlockButton>
						</div>
					</>
				);

			case "generating":
				return (
					<>
						<Header
							title={t("events.add_camera.title")}
							onClose={handleClose}
						/>
						<div className="flex items-center justify-center w-full">
							<Icon
								iconName="spinner_outline"
								size="XXLarge"
								color="#999999"
								loading={true}
								onClick={() => undefined}
							/>
						</div>
					</>
				);

			case "api_key":
				return (
					<>
						<Header
							title={t("events.add_camera.confirm_title")}
							onClose={handleClose}
						/>
						<div className="flex flex-col gap-[24px] items-start w-full">
							<ProgressIndicator current={2} total={3} t={t} />
							<p className="text-[16px] font-normal leading-[1.4] tracking-[0.25px] text-[#404040] w-full">
								{t("events.add_camera.confirm_instruction")}
							</p>
							<div className="flex flex-col gap-[12px] items-start w-full">
								<Input
									label={
										<div className="flex items-center gap-[2px]">
											<span>{t("events.add_camera.name_label")}</span>
											<span className="text-[#e90035]">*</span>
										</div>
									}
									value={name}
									hint={t("events.add_camera.name_hint")}
									readOnly
								/>
								<Input
									label={t("events.add_camera.desc_label")}
									value={desc}
									readOnly
								/>
							</div>
							{/* API Key Card */}
							<div className="border border-[#dfdfdf] rounded-[8px] px-[16px] py-[20px] w-full flex flex-col gap-[24px]">
								<div className="flex flex-col gap-[16px] w-full">
									<div className="flex flex-col gap-[8px] w-full">
										<p className="text-[14px] font-medium leading-[1.35] text-[#999]">
											{t("events.add_camera.id_label")}
										</p>
										<div className="flex items-start justify-between w-full">
											<p className="text-[16px] font-normal leading-[1.4] tracking-[0.25px] text-[#404040]">
												{agentId}
											</p>
											<button
												type="button"
												onClick={() => copyToClipboard(agentId, "id")}
											>
												<span className="relative w-icon-size-2 h-icon-size-2">
													<span
														className={copiedField === "id" ? "hidden" : ""}
													>
														<Icon
															iconName="copy_outline"
															size="Medium"
															color="#404040"
														/>
													</span>
													<span
														className={copiedField === "id" ? "" : "hidden"}
													>
														<Icon
															iconName="check_outline"
															size="Medium"
															color="#1EB44A"
														/>
													</span>
												</span>
											</button>
										</div>
									</div>
									<div className="flex flex-col gap-[8px] w-full">
										<p className="text-[14px] font-medium leading-[1.35] text-[#999]">
											{t("events.add_camera.api_key_label")}
										</p>
										<div className="flex items-start justify-between w-full">
											<p className="text-[16px] font-normal leading-[1.4] tracking-[0.25px] text-[#404040]">
												{apiKey}
											</p>
											<button
												type="button"
												onClick={() => copyToClipboard(apiKey, "key")}
											>
												<span className="relative w-icon-size-2 h-icon-size-2">
													<span
														className={copiedField === "key" ? "hidden" : ""}
													>
														<Icon
															iconName="copy_outline"
															size="Medium"
															color="#404040"
														/>
													</span>
													<span
														className={copiedField === "key" ? "" : "hidden"}
													>
														<Icon
															iconName="check_outline"
															size="Medium"
															color="#1EB44A"
														/>
													</span>
												</span>
											</button>
										</div>
									</div>
								</div>
								<button
									type="button"
									className="flex items-center gap-[4px]"
									onClick={() =>
										copyToClipboard(`ID: ${agentId}\nAPI Key: ${apiKey}`, "all")
									}
								>
									<span className="relative w-icon-size-1 h-icon-size-1">
										<span className={copiedField === "all" ? "hidden" : ""}>
											<Icon
												iconName="copy_outline"
												size="Small"
												color="#404040"
											/>
										</span>
										<span className={copiedField === "all" ? "" : "hidden"}>
											<Icon
												iconName="check_outline"
												size="Small"
												color="#1EB44A"
											/>
										</span>
									</span>
									<span
										className={`text-[14px] font-medium leading-[1.25] ${copiedField === "all" ? "text-[#1EB44A]" : "text-[#404040]"}`}
									>
										{copiedField === "all"
											? t("events.create_dialog.copied")
											: t("events.add_camera.copy_all")}
									</span>
								</button>
							</div>
							{/* Warning Banner */}
							<div className="flex gap-[10px] items-center px-[12px] py-[16px] rounded-[8px] bg-[#fff8e6] w-full">
								<Icon iconName="warning_outline" size="Small" color="#b58100" />
								<p className="flex-1 text-[14px] font-normal leading-[1.4] tracking-[0.25px] text-[#b58100]">
									{t("events.add_camera.api_key_warning")}
								</p>
							</div>
						</div>
						<div className="flex items-center justify-end w-full">
							<BlockButton
								type="Primary"
								size="Large"
								width="113px"
								onClick={handleConnect}
							>
								{t("events.add_camera.connect")}
							</BlockButton>
						</div>
					</>
				);

			case "connecting":
				return (
					<>
						<Header
							title={t("events.add_camera.connecting_title")}
							onClose={handleClose}
						/>
						<AnimatePresence mode="wait">
							<motion.div
								key="connecting-spinner"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0, scale: 0.95 }}
								transition={{ duration: 0.3 }}
								className="flex flex-col items-center w-full"
							>
								<div className="flex flex-col gap-[12px] items-center py-[24px] w-full">
									<Icon
										iconName="spinner_outline"
										size="XXLarge"
										color="#999999"
										loading={true}
										onClick={() => undefined}
									/>
									<div className="flex flex-col gap-[8px] items-start text-center w-full">
										<p className="text-[18px] font-semibold leading-[1.2] tracking-[-0.5px] text-[#404040] w-full">
											{t("events.add_camera.waiting_signal")}
										</p>
										<p className="text-[16px] font-normal leading-[1.4] tracking-[0.25px] text-[#6d6d6d] w-full">
											{t("events.add_camera.waiting_desc")}
										</p>
									</div>
								</div>
							</motion.div>
						</AnimatePresence>
						<div className="flex items-center w-full">
							<BlockButton
								type="Secondary"
								size="Large"
								width="56px"
								onClick={handleClose}
							>
								{t("events.add_camera.close")}
							</BlockButton>
						</div>
					</>
				);

			case "connected":
				return (
					<>
						<Header
							title={t("events.add_camera.connecting_title")}
							onClose={handleClose}
						/>
						<motion.div
							initial={{ opacity: 0, scale: 0.9 }}
							animate={{ opacity: 1, scale: 1 }}
							transition={{ duration: 0.4, ease: "easeOut" }}
							className="w-full"
						>
							<SuccessBanner name={name} t={t} />
						</motion.div>
					</>
				);

			case "calibration":
				return (
					<>
						<Header
							title={t("events.add_camera.calibration_title")}
							onClose={handleClose}
						/>
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.3 }}
							className="w-full"
						>
							<ProgressIndicator current={3} total={3} t={t} />
						</motion.div>
						<div className="flex flex-col gap-[20px] items-start w-full">
							<SuccessBanner name={name} t={t} />
							<motion.div
								initial={{ opacity: 0, y: 30 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
								className="w-full"
							>
								<CourtLinesUpload
									file={calibrationFile}
									onChange={setCalibrationFile}
								/>
							</motion.div>
						</div>
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.4, delay: 0.4, ease: "easeOut" }}
							className="flex items-center justify-between w-full"
						>
							<BlockButton
								type="Secondary"
								size="Large"
								width="56px"
								onClick={handleFinish}
							>
								{t("events.add_camera.skip")}
							</BlockButton>
							<BlockButton
								type="Primary"
								size="Large"
								width="104px"
								onClick={handleFinish}
								disabled={!calibrationFile}
							>
								{t("events.add_camera.save_finish")}
							</BlockButton>
						</motion.div>
					</>
				);

			case "timeout":
				return (
					<>
						<Header
							title={t("events.add_camera.connecting_title")}
							onClose={handleClose}
						/>
						<div className="flex flex-col gap-[20px] items-start w-full">
							<div className="flex flex-col gap-[12px] items-center p-[20px] rounded-[4px] w-full">
								<Icon
									iconName="circle_cross_outline"
									size="XXLarge"
									color="#e90035"
								/>
								<p className="text-[18px] font-semibold leading-[1.2] tracking-[-0.5px] text-[#e90035] text-center">
									{t("events.add_camera.timeout_title")}
								</p>
								<p className="text-[16px] font-normal leading-[1.4] tracking-[0.25px] text-[#404040] text-center w-full">
									{t("events.add_camera.timeout_desc")}
								</p>
							</div>
							<div className="bg-[#f5f5f5] flex flex-col gap-[10px] items-start p-[16px] rounded-[4px] w-full">
								<p className="text-[16px] font-medium leading-[1.25] text-black text-center">
									{t("events.add_camera.timeout_check_title")}
								</p>
								<ol className="list-decimal ml-[21px] text-[14px] font-normal leading-[1.4] tracking-[0.25px] text-[#404040]">
									<li>{t("events.add_camera.timeout_check_1")}</li>
									<li>{t("events.add_camera.timeout_check_2")}</li>
								</ol>
							</div>
						</div>
						<div className="flex items-center w-full">
							<BlockButton
								type="Primary"
								size="Large"
								width="160px"
								icon="chevrons_left"
								onClick={() => setStep("api_key")}
							>
								{t("events.add_camera.back_to_key")}
							</BlockButton>
						</div>
					</>
				);

			case "leave_confirm":
				// Rendered as a separate overlay — fall through to show the underlying step
				return renderStep(prevStepRef.current);
		}
	};

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
			onClick={(e) => {
				if (e.target === e.currentTarget) handleClose();
			}}
		>
			<div className="bg-white flex flex-col gap-[32px] items-start overflow-clip px-[24px] py-[32px] rounded-[8px] w-full max-w-[600px]">
				{renderStep(step)}
			</div>

			{/* Leave confirmation overlay on top of the main modal */}
			{step === "leave_confirm" && (
				<div
					className="fixed inset-0 z-[51] flex items-center justify-center"
					style={{ backgroundColor: "#2D343F4D" }}
					onClick={(e) => {
						if (e.target === e.currentTarget) setStep(prevStepRef.current);
					}}
				>
					<div className="bg-white flex flex-col gap-[32px] items-start overflow-clip px-[24px] py-[32px] rounded-[8px] w-full max-w-[400px]">
						<Header
							title={t("events.add_camera.leave_title")}
							onClose={() => setStep(prevStepRef.current)}
						/>
						<p className="text-[16px] font-normal leading-[1.4] tracking-[0.25px] text-[#404040] w-full">
							{t("events.add_camera.leave_desc")}
						</p>
						<div className="flex gap-[12px] items-center justify-end w-full">
							<BlockButton
								type="Critical"
								size="Large"
								width="50%"
								onClick={() => {
									stopPolling();
									onClose();
								}}
							>
								{t("events.add_camera.leave")}
							</BlockButton>
							<BlockButton
								type="Secondary"
								size="Large"
								width="50%"
								onClick={() => setStep(prevStepRef.current)}
							>
								{t("events.add_camera.stay")}
							</BlockButton>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

const Header = ({ title, onClose }: { title: string; onClose: () => void }) => (
	<div className="flex items-center justify-between w-full">
		<p className="flex-1 font-medium leading-[1.2] text-[#404040] text-[24px] tracking-[-0.5px]">
			{title}
		</p>
		<IconButton
			type="Tertiary"
			size="Small"
			icon="cross_outline"
			onClick={onClose}
		/>
	</div>
);

const SuccessBanner = ({
	name,
	t,
}: {
	name: string;
	t: (key: string, opts?: any) => string;
}) => (
	<div className="flex gap-[12px] items-center p-[20px] rounded-[4px] bg-[#e9f9ee] w-full">
		<Icon iconName="check_outline" size="XLarge" color="#1eb44a" />
		<div className="flex flex-1 flex-col gap-[4px] items-start justify-center">
			<p className="text-[18px] font-semibold leading-[1.2] tracking-[-0.5px] text-[#1eb44a]">
				{t("events.add_camera.connected", { name })}
			</p>
			<p className="text-[16px] font-normal leading-[1.4] tracking-[0.25px] text-[#404040]">
				{t("events.add_camera.connected_desc")}
			</p>
		</div>
	</div>
);
